import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import {
  RefreshCw, Activity, Users, Globe2, BarChart3, Cpu, Radio,
  EyeOff, Eye, Server, Database, Network, Layers,
} from 'lucide-react';

const RANGES = [
  { id: '5m',  label: '5 min' },
  { id: '1h',  label: '1 h'   },
  { id: '6h',  label: '6 h'   },
  { id: '24h', label: '24 h'  },
];

const TABS = [
  { id: 'overview', label: 'Visão Geral',    icon: Activity },
  { id: 'clients',  label: 'Top Clientes',   icon: Users },
  { id: 'domains',  label: 'Sites Acessados', icon: Globe2 },
  { id: 'zones',    label: 'Por Zona',       icon: Layers },
  { id: 'servers',  label: 'Servidores',     icon: Server },
  { id: 'live',     label: 'Logs ao Vivo',   icon: Radio },
];

const COL = {
  ok: 'var(--ok)', warn: 'var(--warn)', err: 'var(--err)',
  accent: 'var(--accent)', accent2: 'var(--accent-2)',
  cyan: '#22d3ee', violet: '#a855f7', orange: '#f97316', mute: 'var(--text-mute)',
};

const qs = (range, internal, extra = '') =>
  `?range=${range}${internal ? '&include_internal=1' : ''}${extra}`;

export default function Monitoring() {
  const [tab, setTab]     = useState('overview');
  const [range, setRange] = useState('1h');
  const [internal, setInternal] = useState(false);

  return (
    <div className="stack-lg">
      <header className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="h1">Monitor DNS</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Telemetria em tempo real dos servidores autoritativos e recursivos.
          </p>
        </div>
        <div className="hrow" style={{ gap: 8 }}>
          <button
            onClick={() => setInternal(v => !v)}
            className={'btn btn-sm ' + (internal ? 'btn-secondary' : 'btn-ghost')}
            title="Tráfego interno = Docker/healthcheck/IXFR do slave. Oculto por padrão."
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {internal ? <Eye size={13}/> : <EyeOff size={13}/>}
            {internal ? 'Interno: visível' : 'Interno: oculto'}
          </button>
          <div className="hrow" style={{ gap: 4, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 2 }}>
            {RANGES.map(r => (
              <button key={r.id} onClick={() => setRange(r.id)}
                className={'btn btn-sm ' + (range === r.id ? 'btn-primary' : 'btn-ghost')}
                style={{ fontSize: 12 }}>{r.label}</button>
            ))}
          </div>
        </div>
      </header>

      <div className="tab-bar">
        {TABS.map(t => {
          const Ic = t.icon;
          return (
            <div key={t.id} className={'tab ' + (tab === t.id ? 'active' : '')} onClick={() => setTab(t.id)}>
              <Ic size={14} style={{ marginRight: 6 }}/> {t.label}
            </div>
          );
        })}
      </div>

      {tab === 'overview' && <Overview range={range} internal={internal}/>}
      {tab === 'clients'  && <TopClients range={range} internal={internal}/>}
      {tab === 'domains'  && <TopDomains range={range} internal={internal}/>}
      {tab === 'zones'    && <ByZone/>}
      {tab === 'servers'  && <Servers/>}
      {tab === 'live'     && <LiveLog/>}
    </div>
  );
}

// ═══════════════ Componentes de gráfico ═══════════════
function Donut({ segments, size = 132, thickness = 20, centerLabel, centerSub }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  const r = (size - thickness) / 2, C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <g transform={`rotate(-90 ${size/2} ${size/2})`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--bg)" strokeWidth={thickness}/>
        {segments.filter(s => s.value > 0).map((s, i) => {
          const frac = s.value / total, dash = frac * C;
          const el = (
            <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
              stroke={s.color} strokeWidth={thickness}
              strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc * C}
              style={{ transition: 'stroke-dasharray .4s, stroke-dashoffset .4s' }}/>
          );
          acc += frac; return el;
        })}
      </g>
      {centerLabel !== undefined && <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="700" fill="var(--text)" fontFamily="var(--font-mono)">{centerLabel}</text>}
      {centerSub && <text x="50%" y="62%" textAnchor="middle" fontSize="9.5" fill="var(--text-mute)" style={{ textTransform: 'uppercase', letterSpacing: '.08em' }}>{centerSub}</text>}
    </svg>
  );
}

function Legend({ items }) {
  return (
    <div className="stack-sm" style={{ flex: 1, minWidth: 130 }}>
      {items.filter(i => i.value > 0).map(i => (
        <div key={i.label} className="row-between" style={{ fontSize: 12.5 }}>
          <span className="hrow" style={{ gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: i.color, flexShrink: 0 }}/> {i.label}</span>
          <span className="mono dim">{i.value.toLocaleString('pt-BR')}</span>
        </div>
      ))}
    </div>
  );
}

function SplitBar({ a, b }) {
  const total = Math.max(1, a.value + b.value);
  return (
    <div>
      <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: 'var(--bg)' }}>
        <div style={{ width: `${a.value / total * 100}%`, background: a.color, transition: 'width .3s' }}/>
        <div style={{ width: `${b.value / total * 100}%`, background: b.color, opacity: .85, transition: 'width .3s' }}/>
      </div>
      <div className="row-between" style={{ marginTop: 8, fontSize: 12 }}>
        <span className="hrow" style={{ gap: 6 }}><span className="dot" style={{ background: a.color }}/> {a.label} <span className="mono dim">{a.value.toLocaleString('pt-BR')}</span></span>
        <span className="hrow" style={{ gap: 6 }}><span className="mono dim">{b.value.toLocaleString('pt-BR')}</span> {b.label} <span className="dot" style={{ background: b.color }}/></span>
      </div>
    </div>
  );
}

function Heatmap({ hours, peak }) {
  const max = Math.max(peak || 0, 1);
  const nowH = new Date().getUTCHours();
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 3 }}>
        {hours.map((v, h) => {
          const intensity = v / max;
          const bg = v === 0 ? 'var(--bg)' : `color-mix(in srgb, var(--accent) ${Math.round(15 + intensity * 85)}%, var(--bg))`;
          return (
            <div key={h} title={`${String(h).padStart(2,'0')}h UTC — ${v} queries`}
              style={{ height: 34, borderRadius: 4, background: bg,
                border: h === nowH ? '1px solid var(--accent)' : '1px solid var(--border)',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 2 }}>
              <span style={{ fontSize: 8.5, color: 'var(--text-mute)', fontFamily: 'var(--font-mono)' }}>{h % 3 === 0 ? h : ''}</span>
            </div>
          );
        })}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>Hora do dia (UTC) · célula destacada = hora atual</div>
    </div>
  );
}

// ═══════════════ Overview ═══════════════
function Overview({ range, internal }) {
  const [d, setD] = useState({ summary: null, timeseries: [], types: [], resolvers: [], servers: [], heatmap: null });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, ts, qt, rv, ss, hm] = await Promise.all([
        api.get(`/dns/monitor/summary${qs(range, internal)}`),
        api.get(`/dns/monitor/timeseries${qs(range, internal, '&buckets=48')}`),
        api.get(`/dns/monitor/query-types${qs(range, internal)}`),
        api.get('/dns/monitor/resolvers'),
        api.get('/dns/monitor/server-stats'),
        api.get(`/dns/monitor/heatmap${qs(range, internal)}`),
      ]);
      setD({
        summary: s.data, timeseries: ts.data.buckets || [], types: qt.data.types || [],
        resolvers: rv.data.resolvers || [], servers: ss.data.servers || [], heatmap: hm.data,
      });
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range, internal]);

  const s = d.summary;
  const ext = s?.external_queries ?? 0, intc = s?.internal_queries ?? 0;
  const noData = s && s.total_queries === 0;

  // agrega respostas master+slave (acumulado desde o boot)
  const R = d.servers.reduce((a, sv) => {
    const r = sv.responses || {};
    a.success += r.success || 0; a.nxdomain += r.nxdomain || 0;
    a.refused += r.refused || 0; a.failures += r.failures || 0;
    return a;
  }, { success: 0, nxdomain: 0, refused: 0, failures: 0 });
  const respSegs = [
    { label: 'Sucesso',  value: R.success,  color: COL.ok },
    { label: 'NXDOMAIN', value: R.nxdomain, color: COL.warn },
    { label: 'Recusado', value: R.refused,  color: COL.violet },
    { label: 'Falhas',   value: R.failures, color: COL.err },
  ];
  const respTotal = respSegs.reduce((a, x) => a + x.value, 0);
  const cache = d.servers.find(x => x.type === 'master')?.cache;

  const typeColors = [COL.accent, COL.accent2, COL.ok, COL.warn, COL.cyan, COL.violet, COL.orange, COL.mute];
  const typeSegs = d.types.slice(0, 8).map((t, i) => ({ label: t.type, value: t.count, color: typeColors[i % typeColors.length] }));

  return (
    <div className="stack">
      <div className="grid-4">
        <Kpi label={internal ? 'QPS (média)' : 'QPS externo'} value={s?.qps_current ?? '–'} spark={s?.qps_sparkline}/>
        <Kpi label={internal ? 'Total de queries' : 'Queries de assinantes'} value={s ? s.total_queries.toLocaleString('pt-BR') : '–'}/>
        <Kpi label="Clientes únicos"  value={s?.clients_unique ?? '–'}/>
        <Kpi label="Janela analisada" value={s ? formatSpan(s.span_seconds) : '–'}/>
      </div>

      {noData && !internal && (
        <div className="card" style={{ borderColor: 'var(--warn)', background: 'rgba(245,158,11,.06)' }}>
          <div className="card-body hrow" style={{ gap: 10, fontSize: 13 }}>
            <Activity size={16} color="var(--warn)"/>
            <span>Sem tráfego <strong>externo</strong> no período (domínios podem não estar delegados ainda). Clique em <strong>“Interno: oculto”</strong> para ver as queries internas.</span>
          </div>
        </div>
      )}

      {/* Origem + Respostas + Cache */}
      <div className="grid-3">
        <div className="card">
          <div className="card-header"><Network size={15} color="var(--accent)"/><h3 className="h3">Origem do tráfego</h3></div>
          <div className="card-body">
            {s ? <>
              <SplitBar a={{ label: 'Externo', value: ext, color: COL.accent }} b={{ label: 'Interno', value: intc, color: COL.mute }}/>
              <div style={{ height: 12 }}/>
              <SplitBar a={{ label: 'IPv4', value: s.ipv4_queries || 0, color: COL.accent }} b={{ label: 'IPv6', value: s.ipv6_queries || 0, color: COL.cyan }}/>
              <div style={{ height: 12 }}/>
              <SplitBar a={{ label: 'Recursão', value: s.recursion_queries || 0, color: COL.accent2 }} b={{ label: 'Autoritativo', value: s.auth_queries || 0, color: COL.ok }}/>
            </> : <div className="dim">Carregando…</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><BarChart3 size={15} color="var(--accent)"/><h3 className="h3">Respostas (RCODE)</h3><span className="badge muted" style={{marginLeft:'auto', fontSize:9.5}}>acum.</span></div>
          <div className="card-body hrow" style={{ gap: 14, alignItems: 'center' }}>
            <Donut segments={respSegs} centerLabel={respTotal.toLocaleString('pt-BR')} centerSub="respostas"/>
            <Legend items={respSegs}/>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><Database size={15} color="var(--accent)"/><h3 className="h3">Cache do resolver</h3></div>
          <div className="card-body hrow" style={{ gap: 14, alignItems: 'center', justifyContent: 'center' }}>
            {cache ? <>
              <Donut size={132}
                segments={[{ label: 'hit', value: cache.hits, color: COL.ok }, { label: 'miss', value: cache.misses, color: COL.mute }]}
                centerLabel={cache.ratio != null ? `${cache.ratio}%` : '–'} centerSub="hit rate"/>
              <div className="stack-sm" style={{ fontSize: 12.5 }}>
                <div className="hrow" style={{ gap: 7 }}><span className="dot" style={{ background: COL.ok }}/> Hits <span className="mono dim">{cache.hits.toLocaleString('pt-BR')}</span></div>
                <div className="hrow" style={{ gap: 7 }}><span className="dot" style={{ background: COL.mute }}/> Misses <span className="mono dim">{cache.misses.toLocaleString('pt-BR')}</span></div>
              </div>
            </> : <div className="dim">Sem dados de cache</div>}
          </div>
        </div>
      </div>

      {/* Volume */}
      <div className="card">
        <div className="card-header">
          <BarChart3 size={15} color="var(--accent)"/><h3 className="h3">Volume de queries · {range}</h3>
          <div className="card-actions"><button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/> Atualizar</button></div>
        </div>
        <div className="card-body">
          {d.timeseries.length > 0 ? <AreaChart buckets={d.timeseries}/> : <div className="empty-state" style={{padding:32}}><div className="empty-desc">Sem dados no período</div></div>}
        </div>
      </div>

      {/* Heatmap */}
      {d.heatmap && (
        <div className="card">
          <div className="card-header"><Activity size={15} color="var(--accent)"/><h3 className="h3">Atividade por hora do dia</h3><span className="badge muted" style={{marginLeft:'auto'}}>pico {d.heatmap.peak}/h</span></div>
          <div className="card-body"><Heatmap hours={d.heatmap.hours} peak={d.heatmap.peak}/></div>
        </div>
      )}

      {/* Tipos (donut) + Resolvers */}
      <div className="grid-2">
        <div className="card">
          <div className="card-header"><Activity size={15} color="var(--accent)"/><h3 className="h3">Tipos de query</h3><span className="badge muted" style={{marginLeft:'auto'}}>{d.types.length} tipos</span></div>
          <div className="card-body hrow" style={{ gap: 16, alignItems: 'center' }}>
            {typeSegs.length > 0 ? <>
              <Donut segments={typeSegs} centerLabel={d.types.reduce((a,t)=>a+t.count,0).toLocaleString('pt-BR')} centerSub="queries"/>
              <Legend items={typeSegs}/>
            </> : <div className="dim">Sem dados</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><Cpu size={15} color="var(--accent)"/><h3 className="h3">Resolvers</h3></div>
          <div className="card-body stack-sm">
            {d.resolvers.map(r => (
              <div key={r.name} style={{padding:'10px 12px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8}}>
                <div className="row-between">
                  <div><div style={{fontSize:13, fontWeight:600}}>{r.name}</div><div className="mono dim" style={{fontSize:11, marginTop:2}}>{r.ip} · {r.type}</div></div>
                  <div style={{textAlign:'right'}}><div className="mono" style={{fontSize:18, fontWeight:700}}>{r.qps}</div><div className="dim" style={{fontSize:10.5}}>qps últimos 60s</div></div>
                </div>
                <div className="hrow" style={{gap:10, marginTop:8, fontSize:11}}>
                  <span className={'badge ' + (r.status === 'ok' ? 'ok' : 'err')}><span className={'dot ' + r.status}/> {r.status === 'ok' ? 'Online' : 'Falha'}</span>
                  <span className="dim">total: <span className="mono">{r.total_queries.toLocaleString('pt-BR')}</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════ Por Zona ═══════════════
function ByZone() {
  const [data, setData] = useState({ zones: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const load = async () => { setLoading(true); try { const r = await api.get('/dns/monitor/by-zone'); setData(r.data); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const max = data.zones[0]?.count || 1;
  return (
    <div className="card">
      <div className="card-header">
        <Layers size={15} color="var(--accent)"/><h3 className="h3">Queries por zona</h3>
        <span className="badge muted">{data.total.toLocaleString('pt-BR')} queries · desde o boot</span>
        <div className="card-actions"><button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/></button></div>
      </div>
      {data.zones.length === 0 ? <div className="empty-state"><div className="empty-desc">Sem dados de zona</div></div> : (
        <table className="table">
          <thead><tr><th style={{width:44}}>#</th><th>Zona</th><th style={{width:90}}>Queries</th><th style={{width:280}}>Participação</th></tr></thead>
          <tbody>
            {data.zones.map((z, i) => (
              <tr key={z.zone}>
                <td><Rank i={i}/></td>
                <td className="mono" style={{ fontSize: 12.5, wordBreak: 'break-all' }}>{z.zone}</td>
                <td className="mono">{z.count.toLocaleString('pt-BR')}</td>
                <td><div className="hrow" style={{ gap: 8 }}>
                  <div style={{ flex: 1, height: 7, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${z.count / max * 100}%`, background: 'var(--accent)', borderRadius: 4 }}/>
                  </div>
                  <span className="mono dim" style={{ fontSize: 11.5, minWidth: 44, textAlign: 'right' }}>{z.share_pct}%</span>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════ Servidores ═══════════════
function Servers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => { setLoading(true); try { const r = await api.get('/dns/monitor/server-stats'); setServers(r.data.servers || []); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

  return (
    <div className="stack">
      <div className="row-between">
        <p className="dim" style={{ fontSize: 13 }}>Contadores acumulados desde o boot de cada servidor (BIND <code>rndc stats</code>).</p>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/> Atualizar</button>
      </div>
      <div className="grid-2">
        {servers.map(sv => {
          const r = sv.responses || {}, t = sv.transport || {}, c = sv.cache || {};
          const respSegs = [
            { label: 'Sucesso', value: r.success || 0, color: COL.ok },
            { label: 'NXDOMAIN', value: r.nxdomain || 0, color: COL.warn },
            { label: 'Recusado', value: r.refused || 0, color: COL.violet },
            { label: 'Falhas', value: r.failures || 0, color: COL.err },
          ];
          return (
            <div key={sv.name} className="card">
              <div className="card-header">
                <Server size={15} color="var(--accent)"/>
                <h3 className="h3">{sv.name}</h3>
                <span className={'badge ' + (sv.up ? 'ok' : 'err')} style={{ marginLeft: 'auto' }}><span className={'dot ' + (sv.up ? 'ok' : 'err')}/>{sv.up ? 'online' : 'offline'}</span>
              </div>
              <div className="card-body stack">
                <div className="hrow" style={{ gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                  <span className="badge muted mono">{sv.ip}</span>
                  <span className="badge muted">{sv.type}</span>
                  <span className="badge accent">{sv.zones} zonas</span>
                </div>
                <div className="hrow" style={{ gap: 14, alignItems: 'center' }}>
                  <Donut segments={respSegs} size={120} thickness={18}
                    centerLabel={(respSegs.reduce((a,x)=>a+x.value,0)).toLocaleString('pt-BR')} centerSub="resp."/>
                  <Legend items={respSegs}/>
                </div>
                <div className="grid-2" style={{ gap: 10 }}>
                  <Mini label="Cache hit" value={c.ratio != null ? `${c.ratio}%` : '–'} sub={`${(c.hits||0)} hits / ${(c.misses||0)} miss`}/>
                  <Mini label="Recursão" value={(r.recursion||0).toLocaleString('pt-BR')} sub="queries com recursão"/>
                </div>
                <div>
                  <div className="dim" style={{ fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Transporte</div>
                  <div className="grid-2" style={{ gap: 8 }}>
                    <SplitBar a={{ label: 'UDP', value: t.udp || 0, color: COL.accent }} b={{ label: 'TCP', value: t.tcp || 0, color: COL.warn }}/>
                    <SplitBar a={{ label: 'IPv4', value: t.ipv4 || 0, color: COL.accent }} b={{ label: 'IPv6', value: t.ipv6 || 0, color: COL.cyan }}/>
                  </div>
                </div>
                <div className="hrow" style={{ gap: 14, fontSize: 11.5, color: 'var(--text-mute)', flexWrap: 'wrap' }}>
                  <span>EDNS: <span className="mono" style={{color:'var(--text)'}}>{(t.edns||0).toLocaleString('pt-BR')}</span></span>
                  <span>TSIG: <span className="mono" style={{color:'var(--text)'}}>{(t.tsig||0).toLocaleString('pt-BR')}</span></span>
                  <span>Duplicadas: <span className="mono" style={{color:'var(--text)'}}>{(r.duplicate||0).toLocaleString('pt-BR')}</span></span>
                </div>
                {sv.boot && <div className="dim" style={{ fontSize: 11 }}>Boot: {sv.boot}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Mini({ label, value, sub }) {
  return (
    <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div className="dim" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
      <div className="mono" style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub && <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Kpi({ label, value, spark }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <div className="stat-value">{value}</div>
      {spark && spark.length > 0 && <Sparkline data={spark} height={28} color="var(--accent)"/>}
    </div>
  );
}

function Sparkline({ data, height = 32, color = 'var(--accent)' }) {
  if (!data || data.length < 2) return <div style={{height}}/>;
  const max = Math.max(...data, 1), w = 100, h = height;
  const pts = data.map((v, i) => `${(i / (data.length - 1) * w).toFixed(2)},${(h - (v / max) * h).toFixed(2)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{marginTop:4}}>
      <polyline fill={color} fillOpacity=".15" stroke="none" points={`0,${h} ${pts} ${w},${h}`}/>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts}/>
    </svg>
  );
}

function AreaChart({ buckets }) {
  if (!buckets || buckets.length < 2) return null;
  const w = 800, h = 200, pad = 28;
  const vals = buckets.map(b => b.total);
  const max = Math.max(...vals, 1);
  const xStep = (w - pad*2) / (buckets.length - 1);
  const pts = buckets.map((b, i) => `${pad + i*xStep},${h - pad - (b.total/max)*(h - pad*2)}`).join(' ');
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ y: h - pad - f*(h-pad*2), v: Math.round(max * f) }));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" preserveAspectRatio="none" style={{height:200}}>
      <defs><linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--accent)" stopOpacity=".35"/><stop offset="100%" stopColor="var(--accent)" stopOpacity="0"/>
      </linearGradient></defs>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={pad} x2={w-pad} y1={t.y} y2={t.y} stroke="var(--border)" strokeWidth=".5"/>
          <text x={pad-4} y={t.y+3} textAnchor="end" fontSize="9" fill="var(--text-mute)" fontFamily="JetBrains Mono">{t.v}</text>
        </g>
      ))}
      <polyline fill="url(#volGrad)" stroke="none" points={`${pad},${h-pad} ${pts} ${pad + (buckets.length-1)*xStep},${h-pad}`}/>
      <polyline fill="none" stroke="var(--accent)" strokeWidth="1.5" points={pts}/>
    </svg>
  );
}

function Rank({ i }) {
  const medal = ['#fbbf24', '#cbd5e1', '#d97706'][i];
  if (medal) return <span className="mono" style={{ fontWeight: 700, color: medal }}>#{i+1}</span>;
  return <span className="mono dim">{i+1}</span>;
}

// ═══════════════ Top Clientes ═══════════════
function TopClients({ range, internal }) {
  const [data, setData] = useState({ clients: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const load = async () => { setLoading(true); try { const r = await api.get(`/dns/monitor/top-clients${qs(range, internal, '&limit=50')}`); setData(r.data); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range, internal]);
  const maxShare = data.clients[0]?.share_pct || 1;
  return (
    <div className="card">
      <div className="card-header">
        <Users size={15} color="var(--accent)"/><h3 className="h3">Top 50 clientes · {range}</h3>
        <span className="badge muted">{data.total.toLocaleString('pt-BR')} queries</span>
        <div className="card-actions"><button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/></button></div>
      </div>
      {data.clients.length === 0 ? <div className="empty-state"><div className="empty-desc">Sem queries no período{!internal && ' (tente exibir o tráfego interno)'}</div></div> : (
        <table className="table">
          <thead><tr><th style={{width:44}}>#</th><th>Cliente</th><th style={{width:90}}>Queries</th><th style={{width:80}}>Tipo</th><th style={{width:220}}>Participação</th></tr></thead>
          <tbody>
            {data.clients.map((c, i) => (
              <tr key={c.ip}>
                <td><Rank i={i}/></td>
                <td>
                  <div className="mono" style={{fontWeight:600}}>{c.ip}{c.internal && <span className="badge muted" style={{marginLeft:8, fontSize:9.5}}>interno</span>}</div>
                  {c.hostname && <div className="mono dim" style={{fontSize:11, marginTop:2}}>{c.hostname}</div>}
                </td>
                <td className="mono">{c.queries_total.toLocaleString('pt-BR')}</td>
                <td><span className="badge accent mono">{c.top_type}</span></td>
                <td><div className="hrow" style={{gap:8}}>
                  <div style={{flex:1, height:6, background:'var(--bg)', borderRadius:3, overflow:'hidden'}}>
                    <div style={{height:'100%', width:`${Math.min(100, (c.share_pct/maxShare)*100)}%`, background: c.internal ? 'var(--text-mute)' : 'var(--accent)', borderRadius:3}}/>
                  </div>
                  <span className="mono dim" style={{fontSize:11.5, minWidth:48, textAlign:'right'}}>{c.share_pct}%</span>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════ Top Domínios ═══════════════
function TopDomains({ range, internal }) {
  const [data, setData] = useState({ domains: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const load = async () => { setLoading(true); try { const r = await api.get(`/dns/monitor/top-domains${qs(range, internal, '&limit=100')}`); setData(r.data); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range, internal]);
  const filtered = q ? data.domains.filter(d => d.domain.toLowerCase().includes(q.toLowerCase())) : data.domains;
  const maxShare = data.domains[0]?.share_pct || 1;
  return (
    <div className="card">
      <div className="card-header">
        <Globe2 size={15} color="var(--accent)"/><h3 className="h3">Top 100 sites/domínios · {range}</h3>
        <span className="badge muted">{data.total.toLocaleString('pt-BR')} queries</span>
        <div className="card-actions">
          <input className="input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Filtrar…" style={{width:200, padding:'5px 10px', fontSize:12}}/>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/></button>
        </div>
      </div>
      {filtered.length === 0 ? <div className="empty-state"><div className="empty-desc">Sem dados</div></div> : (
        <table className="table">
          <thead><tr><th style={{width:44}}>#</th><th>Domínio consultado</th><th style={{width:90}}>Queries</th><th style={{width:80}}>Tipo</th><th style={{width:180}}>Share</th></tr></thead>
          <tbody>
            {filtered.slice(0, 100).map((d, i) => (
              <tr key={d.domain}>
                <td><Rank i={i}/></td>
                <td className="mono" style={{fontSize:12.5, wordBreak:'break-all'}}>{d.domain}</td>
                <td className="mono">{d.count.toLocaleString('pt-BR')}</td>
                <td><span className="badge muted mono">{d.top_type}</span></td>
                <td><div className="hrow" style={{gap:8}}>
                  <div style={{flex:1, height:6, background:'var(--bg)', borderRadius:3, overflow:'hidden'}}>
                    <div style={{height:'100%', width:`${Math.min(100, (d.share_pct/maxShare)*100)}%`, background:'var(--accent-2)', borderRadius:3}}/>
                  </div>
                  <span className="mono dim" style={{fontSize:11.5, minWidth:44, textAlign:'right'}}>{d.share_pct}%</span>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════ Live Log ═══════════════
function LiveLog() {
  const [queries, setQueries] = useState([]);
  const [paused, setPaused]   = useState(false);
  const seenRef = useRef(new Set());
  const tick = async () => {
    if (paused) return;
    try {
      const r = await api.get('/dns/monitor/recent?limit=80');
      const newOnes = (r.data.queries || []).filter(q => {
        const key = `${q.timestamp}|${q.client_ip}|${q.query_name}|${q.query_type}`;
        if (seenRef.current.has(key)) return false;
        seenRef.current.add(key); return true;
      });
      if (newOnes.length > 0) {
        setQueries(prev => [...newOnes, ...prev].slice(0, 200));
        if (seenRef.current.size > 500) seenRef.current = new Set(Array.from(seenRef.current).slice(-300));
      }
    } catch {}
  };
  useEffect(() => { tick(); const i = setInterval(tick, 3000); return () => clearInterval(i); }, [paused]);
  const typeColors = { A:'accent', AAAA:'accent', PTR:'violet', MX:'ok', TXT:'muted', NS:'info', CNAME:'warn', SRV:'info', CAA:'warn' };
  return (
    <div className="card">
      <div className="card-header">
        <Radio size={15} color={paused ? 'var(--text-mute)' : 'var(--ok)'}/><h3 className="h3">Streaming · queries em tempo real</h3>
        {!paused && <span className="badge ok"><span className="dot ok pulse"/>LIVE</span>}
        <span className="badge muted">{queries.length} na tela</span>
        <div className="card-actions">
          <button className="btn btn-secondary btn-sm" onClick={()=>setPaused(p=>!p)}>{paused ? 'Retomar' : 'Pausar'}</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>{setQueries([]); seenRef.current.clear();}}>Limpar</button>
        </div>
      </div>
      <div style={{maxHeight:520, overflow:'auto', padding:'4px 0'}}>
        {queries.length === 0 ? <div className="empty-state" style={{padding:48}}><div className="empty-desc">Aguardando queries…</div></div>
        : queries.map((q, i) => (
          <div key={i} className="hrow" style={{ padding:'7px 18px', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-mono)', fontSize:11.5, gap:14 }}>
            <span style={{color:'var(--text-mute)', minWidth:90}}>{q.timestamp.split(' ')[1]?.slice(0,12) || q.timestamp}</span>
            <span className={`badge ${typeColors[q.query_type] || 'muted'} mono`} style={{fontSize:10, minWidth:40, justifyContent:'center'}}>{q.query_type}</span>
            <span style={{color:'var(--accent)', minWidth:130}}>{q.client_ip}</span>
            <span style={{flex:1, color:'var(--text)', wordBreak:'break-all'}}>{q.query_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSpan(seconds) {
  if (!seconds) return '–';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds/60)} min`;
  if (seconds < 86400) return `${(seconds/3600).toFixed(1)} h`;
  return `${(seconds/86400).toFixed(1)} d`;
}
